import React, {useEffect, useState} from "react";
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal'
import Form from 'react-bootstrap/Form'
import {useTranslation} from "react-i18next";
import {Alert, FloatingLabel} from "react-bootstrap";
import {login} from "../../util/SecurityUtils";
import {useNavigate} from "react-router-dom";
import {usersApi} from "../../api/users";

interface LoginModalProps {
    isOpen: boolean;
    closeModal: () => void;
}

function LoginModal(props: LoginModalProps) {
    const { t } = useTranslation();
    const [ user, setUser ] = useState({username: "", password: "", confirmPassword: ""})
    const [ createAccount, setCreateAccount ] = useState(false);
    const [ isLoading , setIsLoading ] = useState(false);
    const [ usernameAlreadyExists, setUsernameAlreadyExists ] = useState(false);
    const [ somethingWentWrong, setSomethingWentWrong ] = useState(false);
    const [isUsernameValid, setIsUsernameValid] = useState(true);
    const [incorrectCredentials, setIncorrectCredentials] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        if (props.isOpen === false) {
            setUser({username: "", password: "", confirmPassword: ""})
            setCreateAccount(false)
            setIsLoading(false)
            setUsernameAlreadyExists(false)
            setSomethingWentWrong(false)
            setIsUsernameValid(true)
        }
    }, [props.isOpen]);

    const handleUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const valid = /^[a-zA-Z0-9]*$/.test(value);
        setIsUsernameValid(valid);
        setUsernameAlreadyExists(false);
        setUser({ ...user, username: value });
    };

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setIsLoading(true)
        if (createAccount) {
            usersApi.register({ username: user.username, password: user.password }).then((data) => {
                if (data) {
                    loginAndClose(user.username, user.password)
                }
            }).catch((err) => {
                if (err.response && err.response.status === 403) {
                    setUsernameAlreadyExists(true);
                } else {
                    setSomethingWentWrong(true)
                }
            })
              .finally(() => { setIsLoading(false) });
        } else {
            loginAndClose(user.username, user.password)
        }
    };

    const loginAndClose = (username: string, password: string) => {
        login(username, password).then((success) => {
            props.closeModal()
            if (success) {
                navigate(0)
            }
        }).catch(() => {
            setIncorrectCredentials(true)
            setIsLoading(false)
        })
    }

    const isSubmitButtonDisabled = () => {
        if (isLoading) {
            return true;
        }
        if (!isUsernameValid) return true;
        if (createAccount) {
            return !user.username || !user.password || !user.confirmPassword || user.password !== user.confirmPassword;
        }
        return !user.username || !user.password;
    }

    return (
      <Modal
        show={props.isOpen}
        onHide={props.closeModal}
      >
          <Modal.Header closeButton>
              <Modal.Title>{t(createAccount ? "createAccount" : "login")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
              { createAccount ?
              <Alert variant="info">
                  {t("createAccountInfo")}
              </Alert> : null }
              <Form noValidate onSubmit={handleSubmit}>
                  <Form.Group>
                      <FloatingLabel
                        controlId="floatingUsername"
                        label={t("username")}
                        className="mb-3"
                      >
                          <Form.Control
                            required
                            type="text"
                            onChange={handleUsernameChange}
                            value={user.username}
                            placeholder=""
                          />
                      </FloatingLabel>
                      {!isUsernameValid && (
                        <div className="loss mb-3">
                            {t("usernameOnlyLettersNumbers")}
                        </div>
                      )}
                  </Form.Group>
                  { usernameAlreadyExists ? <div className="loss mb-3">{t("usernameAlreadyExists")}</div> : null}
                  <Form.Group>
                      <FloatingLabel
                        controlId="floatingPassword"
                        label={t("password")}
                        className="mb-3"
                      >
                          <Form.Control required type="password" onChange={e => setUser({...user, password: e.target.value})} value={user.password} placeholder=""/>
                      </FloatingLabel>
                  </Form.Group>
                  {createAccount ?
                    <Form.Group>
                        <FloatingLabel
                          controlId="floatingPasswordConfirmation"
                          label={t("confirmPassword")}
                          className="mb-3"
                        >
                            <Form.Control required type="password" onChange={e => setUser({...user, confirmPassword: e.target.value})} value={user.confirmPassword} placeholder=""/>
                        </FloatingLabel>
                    </Form.Group>
                    : null }
                  { createAccount && user.confirmPassword && user.password !== user.confirmPassword ? <div className="loss mb-3">{t("passwordDoesNotMatch")}</div> : null}
                  { somethingWentWrong ?
                    <Alert variant="danger">
                        {t("somethingWentWrong")}
                    </Alert> : null }
                  { incorrectCredentials ? <div className="loss mb-3">{t("wrongPassword")}</div> : null}
                  <div className="d-flex">
                      <Button type="submit" disabled={isSubmitButtonDisabled()}>{t(createAccount ? "createAccount" : "login")}</Button>
                      { createAccount ? null :
                      <Button type="button" variant="secondary" className="ms-auto" onClick={() => setCreateAccount(true)}>{t("createAccount")}</Button> }
                  </div>
              </Form>
          </Modal.Body>
      </Modal>
    );
}

export default LoginModal
